import React, { useEffect, useContext, useState, useCallback, useRef } from 'react';

import PresentPC from 'components/pages/Info/PresentPC';
import PresentSP from 'components/pages/Info/PresentSP';

import { AddressSearchApi } from 'utils/api';
import { DROPDOWN_INITIAL_ITEM, CHOICES_MAP } from 'utils/constant';
import { 
  isSchemaValid,
  isCustomValid,
  useWindowDimensions,
  useDirectTransCheck,
  isFatalApiError,
  sortErrors,
  useIECheck,
  postHeightToParent,
} from 'utils';

import AppContext from 'fluxes/context';
import { UPDATE_INFO, CHANGE_VALID } from 'fluxes/actions';

const Info = () => {
  const path = window.location.pathname;
  const { state, dispatch } = useContext(AppContext);
  useDirectTransCheck({ state });

  const [ corpName, setCorpName ] = useState(state.corpName);
  const [ facilityName, setFacilityName ] = useState(state.facilityName);
  const [ facilityType, setFacilityType ] = useState(state.facilityType);
  const [ capacity, setCapacity ] = useState(state.capacity);
  const [ userName, setUserName ] = useState(state.userName);  
  const [ tel, setTel ] = useState(state.tel);
  const [ email, setEmail ] = useState(state.email);
  const [ emailConf, setEmailConf ] = useState(state.emailConf);
  const [ zip, setZip ] = useState(state.zip);
  const [ municipality, setMunicipality ] = useState(state.municipality);
  const [ houseNumber, setHouseNumber ] = useState(state.houseNumber);
  const [ addressNames, setAddressNames ] = useState([]);
  const [ isOpenAddress, setIsOpenAddress ] = useState(false);

  const { width } = useWindowDimensions();
  const isIE = useIECheck();
  const isSp = width <= 640 && !isIE;
  const pageEl = useRef(null);

  let {
    capacityList,
  } = CHOICES_MAP;

  // SP用にダミーを削除
  const changeSpList = (list) => {
    return list.filter(item => item.value !== DROPDOWN_INITIAL_ITEM.value);
  };

  if(isSp) {
    capacityList = changeSpList(capacityList);
  }

  // 入力チェック
  const initialValid = { valid: state.validMap[path], errorMessages: [] };
  const [ valids, setValids ] = useState({
    schema: initialValid,
    checkEmailMatch: initialValid,
  });
  // エラーメッセージを表示する項目をいれる
  const [ isShowError, setIsShowError ] = useState({
    corpName: false,
    facilityName: false,
    facilityType: false,
    capacity: false,
    userName: false,
    tel: false,
    email: false,
    emailConf: false,
    zip: false,
    municipality: false,
    houseNumber: false,
  });
  const [ isApiError, setIsApiError ] = useState(false);
  const valid = Object.keys(valids).every(key => valids[key].valid);
  const dispErrors = useCallback(() => {
    const { schema, checkEmailMatch } = valids;
    const errors = sortErrors([
      ...schema.errorMessages,
      ...checkEmailMatch.errorMessages,
    ]);
    const errorMap = errors.reduce((obj, error) => {
      const { target, message } = error;
      if(!isShowError[target] || Object.keys(obj).includes(target)) return obj;
      return { ...obj, [target]: message };
    }, {});
    console.log({ errorMap });
    return errorMap;
  }, [ isShowError, valids ]);

  // 入力チェック
  useEffect(()=> {

    // エラーチェックを実行
    const [ isValid, newErrorMessages ] = isSchemaValid({
      target: {
        corpName,
        facilityName,
        facilityType,
        capacity,
        userName,
        tel,
        email,
        emailConf,
        zip,
        municipality,
        houseNumber,
      },
    });
    console.log({ isValid, newErrorMessages });
    // erros更新
    const newValid = { schema: { valid: isValid, errorMessages: newErrorMessages } };
    setValids(org => ({ ...org, ...newValid }));

  }, [ corpName, email, tel, userName, zip, facilityName, facilityType, capacity, isShowError, emailConf, municipality, houseNumber ]);

  // メールアドレスが完全一致しているかチェック
  useEffect(()=>{
    const validation = async () => {
      // エラーチェックを実行
      const [ isValid, newErrorMessages ] = await isCustomValid({
        checks: [ 'checkEmailMatch' ],
        target: { email, emailConf },
      });
      console.log('valid:custom:checkEmailMatch', { isValid, newErrorMessages });
      
      // erros更新
      const newValid = { checkEmailMatch: { valid: isValid, errorMessages: newErrorMessages } };
      setValids(org => ({ ...org, ...newValid }));
    };
    validation();
  }, [ email, emailConf ]);

  // LP埋め込み時の高さ送信
  useEffect(() => {
    if(state.lpMode){
      postHeightToParent(state.lpOrigin, pageEl.current.clientHeight);
    }
  }, [ state.lpMode, state.lpOrigin, pageEl ]);

  // プルダウン両対応用
  const forHandleChange = clickVal => {
    return isSp ? clickVal.target.value : clickVal; 
  };
  
  
  /* onChangeイベント群 */

  // 会社名変更時
  const handleChangeCorpName = async (e)=>{
    setCorpName(e.target.value);
    // corpNameエラー表示開始
    if(!isShowError.corpName) {
      setIsShowError({ ...isShowError, corpName: true });
    }
  };

  // 施設名変更時
  const handleChangeFacilityName = (e) => {
    setFacilityName(e.target.value);
    // facilityNameエラー表示開始
    if(!isShowError.userName) {
      setIsShowError({ ...isShowError, facilityName: true });
    }
  };

  const handleChangeFacilityType = e => {
    const val = e.target.value;
    if(facilityType.includes(val)) {
      setFacilityType(facilityType.filter(item => item !== val));
    } else {
      setFacilityType([ ...facilityType, val ]);
    }
    // facilityTypeエラー表示開始
    if(!isShowError.facilityType) {
      setIsShowError({ ...isShowError, facilityType: true });
    }
  };

  const typeList = {
    title: 'facilityTypeList',
    typeState: facilityType,
    stateOnChange: handleChangeFacilityType,
  };

  // 定員人数の変更時
  const handleChangeCapacity = clickVal => {
    setCapacity(forHandleChange(clickVal));
    // capacityエラー表示開始
    if(!isShowError.capacity) {
      setIsShowError({ ...isShowError, capacity: true });
    }
  };


  // ご担当者名変更時
  const handleChangeUserName = (e)=>{
    setUserName(e.target.value);
    // userNameエラー表示開始
    if(!isShowError.userName) {
      setIsShowError({ ...isShowError, userName: true });
    }
  };

  // 電話番号変更時
  const handleChangeTel = (e)=>{
    setTel(e.target.value);
    // telエラー表示開始
    if(!isShowError.tel) {
      setIsShowError({ ...isShowError, tel: true });
    }
  };

  // メールアドレス変更時
  const handleChangeEmail = (e) => {
    setEmail(e.target.value);
    // emailエラー表示開始
    if(!isShowError.email) {
      setIsShowError({ ...isShowError, email: true });
    }
  };
  // メールアドレス(確認)変更時
  const handleChangeEmailConf = (e) => {
    setEmailConf(e.target.value);
    // emailConfエラー表示開始
    if(!isShowError.emailConf) {
      setIsShowError({ ...isShowError, emailConf: true });
    }
  };


  // 郵便番号変更時
  const handleChangeZip = async (e) => {
    console.log( `zipCd:${e.target.value}` );
    const zipCd = e.target.value;
    setZip(zipCd);
    
    // 郵便番号エラー表示開始
    if(!isShowError.zip) {
      setIsShowError({ ...isShowError, zip: true });
    }

    // 住所サジェスト
    const regex = /^([0-9]{3}-[0-9]{4}|[0-9]{7})$/;
    if(regex.test(zipCd)) {
      // 住所検索API
      const AdSearchRet = await AddressSearchApi(zipCd.replace('-', '')).catch(err => {
        // FATELエラーチェック
        const isFatal = isFatalApiError(err);
        setIsApiError(isFatal);
        if(isFatal) throw err;
        // 404でもエラーとはしない
        return [];
      });
      if(AdSearchRet.length > 0 ){
        console.log(AdSearchRet);
        setAddressNames(AdSearchRet);
        setIsOpenAddress(true);
      }
    }

  };
  // 郵便番号サジェストクリック時
  const handleClickAddressSuggest = async( idx ) => {
    setIsOpenAddress(true);
    const syougou = addressNames[idx];
    setMunicipality(syougou);
    setAddressNames([]);
  };
  
  // サジェスト部分以外をクリックしたらサジェストを閉じる
  const handleClickAddressSuggestAway = ()=>{
    setIsOpenAddress(false);
  };

  // 住所(市区町村)変更時
  const handleChangeMunicipality = (e) => {
    setMunicipality(e.target.value);
    // 市区町村エラー表示開始
    if(!isShowError.municipality) {
      setIsShowError({ ...isShowError, municipality: true });
    }
  };

  // 番地変更時
  const handleChangeHouseNumber = (e) => {
    setHouseNumber(e.target.value);
    // 番地エラー表示開始
    if(!isShowError.houseNumber) {
      setIsShowError({ ...isShowError, houseNumber: true });
    }
  };
  
  // ボタン押下
  // state更新処理
  const updateState = () => {
    dispatch({
      type: UPDATE_INFO,
      updInfo: {
        corpName,
        facilityName,
        facilityType,
        capacity,
        userName,
        tel,
        email,
        emailConf,
        zip,
        municipality,
        houseNumber,
      },
    });
  };
  const onClickMainButton = {
    prev: () => {
      updateState();
    },
    next: () => {
      updateState();
      dispatch({
        type: CHANGE_VALID, 
        path,
        valid,
      });
    },
  };
  
  return(
    <div id='pageInfo' ref={pageEl}>
      { isSp ? (
        <PresentSP 
          corpName={corpName}
          facilityName={facilityName}
          userName={userName}
          tel={tel}
          email={email}
          emailConf={emailConf}
          capacity={capacity}
          capacityList={capacityList}
          typeList={typeList}
          zip={zip}
          municipality={municipality}
          houseNumber={houseNumber}
          addressNames={addressNames}
          handleChangeCorpName={handleChangeCorpName}
          handleChangeFacilityName={handleChangeFacilityName}
          handleChangeCapacity={handleChangeCapacity}
          handleChangeUserName={handleChangeUserName}
          handleChangeTel={handleChangeTel}
          handleChangeEmail={handleChangeEmail}
          handleChangeEmailConf={handleChangeEmailConf}
          handleChangeZip={handleChangeZip}
          handleClickAddressSuggest={handleClickAddressSuggest}
          handleChangeMunicipality={handleChangeMunicipality}
          handleChangeHouseNumber={handleChangeHouseNumber}
          onClickMainButton={onClickMainButton}
          valid={valid}
          errorDispMap={dispErrors()}
          isApiError={isApiError}
          handleClickAddressSuggestAway={handleClickAddressSuggestAway}
          isOpenAddress={isOpenAddress}
          lpMode={state.lpMode}
        />
      ) : (
        <PresentPC 
          corpName={corpName}
          facilityName={facilityName}
          userName={userName}
          tel={tel}
          email={email}
          emailConf={emailConf}
          capacity={capacity}
          capacityList={capacityList}
          typeList={typeList}
          zip={zip}
          municipality={municipality}
          houseNumber={houseNumber}
          addressNames={addressNames}
          handleChangeCorpName={handleChangeCorpName}
          handleChangeFacilityName={handleChangeFacilityName}
          handleChangeCapacity={handleChangeCapacity}
          handleChangeUserName={handleChangeUserName}
          handleChangeTel={handleChangeTel}
          handleChangeEmail={handleChangeEmail}
          handleChangeEmailConf={handleChangeEmailConf}
          handleChangeZip={handleChangeZip}
          handleClickAddressSuggest={handleClickAddressSuggest}
          handleChangeMunicipality={handleChangeMunicipality}
          handleChangeHouseNumber={handleChangeHouseNumber}
          onClickMainButton={onClickMainButton}
          valid={valid}
          errorDispMap={dispErrors()}
          isApiError={isApiError}
          handleClickAddressSuggestAway={handleClickAddressSuggestAway}
          isOpenAddress={isOpenAddress}
          lpMode={state.lpMode}
        />
      )}
    </div>
  );   
};

export default Info;